import React, { type FC, useState, useCallback, memo } from "react";
import { Button, RadioGroup, Spinner } from "@heroui/react";
import { type GQL } from "@binale-tech/shared";
import { AnimatePresence, motion, type TargetAndTransition, type Transition } from "framer-motion";
import { FormattedMessage } from "react-intl";
import { useFinApiBankConnectionSet } from "../hooks/useFinApiMutations";
import { FinApiBankConnectionCard } from "./FinApiBankConnectionCard";

const MOTION_INITIAL: TargetAndTransition = { opacity: 0, height: 0, y: -10 };
const MOTION_ANIMATE: TargetAndTransition = { opacity: 1, height: "auto", y: 0 };
const MOTION_EXIT: TargetAndTransition = { opacity: 0, height: 0, y: -10 };
const MOTION_TRANSITION: Transition = { duration: 0.3, ease: "easeOut" };

const getAccountValue = (account: GQL.IFinApiAccount) => String(account.id);

type Props = {
    bankId: string;
    companyId: string;
    connection: Partial<Pick<GQL.IFinApiBankConnection, "id" | "bank" | "accounts">>;
    isVisible: boolean;
    onConfirmed: () => void;
    onError: (error: string) => void;
};

const FinApiAccountSelectionImpl: FC<Props> = ({ bankId, companyId, connection, isVisible, onConfirmed, onError }) => {
    const { id: connectionId } = connection ?? {};
    const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>();

    const [setFinAPIConnection, { loading: isSavingAccountId }] = useFinApiBankConnectionSet();

    const handleConfirm = useCallback(async () => {
        if (!selectedAccountId) {
            return;
        }
        try {
            await setFinAPIConnection({
                variables: {
                    id: bankId,
                    companyId,
                    input: { connectionId: Number(connectionId), accountId: selectedAccountId },
                },
            });
            onConfirmed();
        } catch (error) {
            onError(error.message);
        }
    }, [bankId, companyId, connectionId, selectedAccountId, setFinAPIConnection, onConfirmed, onError]);

    const handleAccountChange = useCallback((v: string) => setSelectedAccountId(parseInt(v, 10)), []);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    key="account-selection"
                    initial={MOTION_INITIAL}
                    animate={MOTION_ANIMATE}
                    exit={MOTION_EXIT}
                    transition={MOTION_TRANSITION}
                    className="w-full overflow-hidden p-4 flex flex-col gap-4"
                >
                    <RadioGroup
                        isDisabled={isSavingAccountId}
                        value={selectedAccountId ? String(selectedAccountId) : undefined}
                        onChange={handleAccountChange}
                    >
                        <FinApiBankConnectionCard connection={connection} getAccountValue={getAccountValue} />
                    </RadioGroup>
                    <Button
                        isDisabled={!selectedAccountId}
                        isPending={isSavingAccountId}
                        onPress={handleConfirm}
                        className="self-end"
                    >
                        {({ isPending }) => (
                            <>
                                {isPending ? <Spinner color="current" size="sm" /> : null}
                                <FormattedMessage id="app.button.confirm" />
                            </>
                        )}
                    </Button>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export const FinApiAccountSelection = memo(FinApiAccountSelectionImpl);
